'use client';

import { useCallback, useMemo } from 'react';
import {
	BigNum,
	SpotBalanceType,
	BASE_PRECISION_EXP,
	PERCENTAGE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	SIX,
} from '@drift-labs/sdk';
import useDriftClient from './useDriftClient';
import useDriftClientIsReady from './useDriftClientIsReady';
import {
	JLP_POOL_JLP_MARKET_INDEX,
	JLP_POOL_USDC_MARKET_INDEX,
} from 'src/environmentVariables/EnvironmentVariables';
import { getApyFromApr } from 'src/utils/borrowLend';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { AprDetails } from './earn/types';
import { useAllMarketInterestData } from './earn/useAllMarketInterestData';
import { matchEnum } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { isAmplifyAccountName } from 'src/utils/amplify';
import useTickedAllSubAccounts from './useTickedAllSubAccounts';
import { getTotalAllTimePnlForUserAccount } from './useTotalPnl';
import { AccountData } from 'src/stores/useDriftAccountsStore';
import { useSpotMarketsSortedByMarketIndex } from './spotMarkets/useSpotMarketsSortedByMarketIndex';
import { useMarginInfoByUserKey } from './query/accountTradingData/useMarginInfo';

const eligibleDepositAmplifyMarkets: { [poolId: number]: number[] } = {
	// only allowed to deposit JLP and borrow USDC for amplify
	1: [JLP_POOL_JLP_MARKET_INDEX],
	// technically allowed to deposit/borrow any LST in the LST pool
	2: [], // not implemented yet
};

const correspondingBorrowMarkets: { [depositMarketIndex: number]: number[] } = {
	[JLP_POOL_JLP_MARKET_INDEX]: [JLP_POOL_USDC_MARKET_INDEX],
};

const MAX_LEVERAGE = 5;

const DEFAULT_AMPLIFY_STATS: AmplifyStats = {
	poolId: 1,
	fullMarketName: 'JLP/USDC',
	depositMarketConfig: undefined,
	borrowMarketConfig: undefined,
	depositMarketSymbol: 'JLP',
	borrowMarketSymbol: 'USDC',
	maxApy: 0,
	maxApyBigNum: BigNum.zero(PERCENTAGE_PRECISION_EXP),
	maxLeverage: 1,
	availableLiquidityQuote: BigNum.zero(QUOTE_PRECISION_EXP),
	availableLiquidityBase: BigNum.zero(SIX),
	userDepositsQuote: BigNum.zero(QUOTE_PRECISION_EXP),
	userDepositsBase: BigNum.zero(SIX),
	userBorrowsQuote: BigNum.zero(QUOTE_PRECISION_EXP),
	userBorrowsBase: BigNum.zero(SIX),
	netValueQuote: BigNum.zero(QUOTE_PRECISION_EXP),
	yieldBreakdown: [],
	apyCalculator: () => ({
		netApy: 0,
		depositApy: 0,
		borrowApy: 0,
		bestBorrowMarketIndex: undefined,
	}),
	userAccountData: undefined,
	userKey: undefined,
	userLeverage: 0,
	userPnl: 0,
};

export interface AmplifyStats {
	poolId: number;
	fullMarketName: string;
	depositMarketConfig: SpotMarketConfig | undefined;
	borrowMarketConfig: SpotMarketConfig | undefined;
	depositMarketSymbol: string;
	borrowMarketSymbol: string;
	maxApy: number;
	maxApyBigNum: BigNum;
	maxLeverage: number;
	availableLiquidityQuote: BigNum;
	availableLiquidityBase: BigNum;
	userDepositsQuote: BigNum;
	userDepositsBase: BigNum;
	userBorrowsQuote: BigNum;
	userBorrowsBase: BigNum;
	netValueQuote: BigNum;
	yieldBreakdown: AprDetails[];
	apyCalculator: (
		depositMarketIndex: number,
		leverage?: number
	) => {
		netApy: number;
		depositApy: number;
		borrowApy: number;
		bestBorrowMarketIndex: number | undefined;
	};
	userAccountData?: AccountData;
	userKey?: string; // userKey that has a position in the given amplify market, if any
	userLeverage?: number;
	userPnl?: number;
}

export const useAmplifyStats = (userKey: string, poolId: number) => {
	const borrowLendMarkets = useDriftStore((s) => s.borrowLendData);
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const { data: allMarketData } = useAllMarketInterestData();
	const allMarketInterestData = allMarketData ?? {};
	const subaccounts = useTickedAllSubAccounts();

	const amplifyAccount = useMemo(() => {
		return subaccounts.find(
			(account) =>
				isAmplifyAccountName(account.name) && account.poolId === poolId
		);
	}, [subaccounts, poolId]);

	const { data: amplifyMarginInfo } = useMarginInfoByUserKey(userKey);

	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	// calculator function for apy, if no leverage is provided, it will return the max apy
	const calculateApy = useCallback(
		(
			depositMarketIndex: number,
			leverage?: number
		): {
			netApy: number;
			depositApy: number;
			borrowApy: number;
			bestBorrowMarketIndex: number | undefined;
		} => {
			if (!driftClientIsReady || !driftClient || !borrowLendMarkets)
				return {
					netApy: 0,
					depositApy: 0,
					borrowApy: 0,
					bestBorrowMarketIndex: undefined,
				};

			const depositMarketData = borrowLendMarkets.find(
				(data) => data.bankIndex === depositMarketIndex
			);

			const baseDepositApr =
				depositMarketData?.depositApr.mul(BigNum.from(100)).toNum() ?? 0;

			const marketInterestData = allMarketInterestData[depositMarketIndex];
			const allBreakdowns = marketInterestData
				? [...marketInterestData.deposit.breakdown]
				: [];
			const additionalApr = allBreakdowns.reduce((acc, curr) => {
				return acc + (curr.apr ?? 0);
			}, 0);

			const totalDepositApr = baseDepositApr + additionalApr;

			const eligibleBorrowMarkets =
				correspondingBorrowMarkets[depositMarketIndex];

			const lowestEligibleBorrowAprInfo = eligibleBorrowMarkets
				.map((marketIndex) => {
					const borrowMarketData = borrowLendMarkets.find(
						(data) => data.bankIndex === marketIndex
					);
					return {
						marketIndex,
						borrowApr: borrowMarketData?.borrowApr
							.mul(BigNum.from(100))
							.toNum(),
					};
				})
				.sort((a, b) => (a.borrowApr ?? 0) - (b.borrowApr ?? 0))[0];

			// hard cap the leverage at 5x for now
			const leverageToUse = leverage ? Math.max(leverage, 1) : MAX_LEVERAGE;

			const leveragedAPR =
				totalDepositApr * leverageToUse -
				(lowestEligibleBorrowAprInfo.borrowApr ?? 0) * (leverageToUse - 1);

			return {
				netApy: getApyFromApr(leveragedAPR, 'hourly'),
				depositApy: getApyFromApr(totalDepositApr, 'hourly'),
				borrowApy: getApyFromApr(
					lowestEligibleBorrowAprInfo.borrowApr ?? 0,
					'hourly'
				),
				bestBorrowMarketIndex: lowestEligibleBorrowAprInfo.marketIndex,
			};
		},
		[
			driftClientIsReady,
			driftClient,
			poolId,
			borrowLendMarkets,
			allMarketInterestData,
		]
	);

	const getAmplifyMarketStats = useMemo(
		() => (): AmplifyStats[] => {
			if (!driftClientIsReady || !driftClient || !borrowLendMarkets) {
				return [DEFAULT_AMPLIFY_STATS];
			}

			const marketData = borrowLendMarkets.filter(
				(data) => data.bankConfig.poolId === poolId
			);

			return marketData
				.filter(
					(market) =>
						eligibleDepositAmplifyMarkets[poolId]?.includes(market.bankIndex) ??
						false
				)
				.map((marketInPool) => {
					const depositMarketConfig =
						spotMarketsSortedByMarketIndex[marketInPool.bankConfig.marketIndex];
					const depositMarketIndex = depositMarketConfig.marketIndex;
					const borrowMarketIndex =
						correspondingBorrowMarkets[marketInPool.bankConfig.marketIndex][0];
					const borrowMarketData = marketData.find(
						(data) =>
							data.bankConfig.marketIndex === borrowMarketIndex &&
							data.bankConfig.poolId === poolId
					);

					const borrowMarketConfig =
						spotMarketsSortedByMarketIndex[borrowMarketIndex];

					const userBorrowBalance = !amplifyAccount
						? {
								balance: BigNum.zero(borrowMarketConfig.precisionExp),
								quoteValue: BigNum.zero(QUOTE_PRECISION_EXP),
							}
						: (amplifyAccount.spotBalances.find(
								(acctBalance) =>
									acctBalance.asset.marketIndex === borrowMarketIndex &&
									matchEnum(acctBalance.balanceType, SpotBalanceType.BORROW)
							) ?? {
								balance: BigNum.zero(borrowMarketConfig.precisionExp),
								quoteValue: BigNum.zero(QUOTE_PRECISION_EXP),
							});

					const userDepositBalance = !amplifyAccount
						? {
								balance: BigNum.zero(depositMarketConfig.precisionExp),
								quoteValue: BigNum.zero(QUOTE_PRECISION_EXP),
							}
						: (amplifyAccount.spotBalances.find(
								(acctBalance) =>
									acctBalance.asset.marketIndex === depositMarketIndex &&
									matchEnum(acctBalance.balanceType, SpotBalanceType.DEPOSIT)
							) ?? {
								balance: BigNum.zero(depositMarketConfig.precisionExp),
								quoteValue: BigNum.zero(QUOTE_PRECISION_EXP),
							});

					const userPnl = !amplifyAccount?.client
						? 0
						: amplifyAccount.client
							? getTotalAllTimePnlForUserAccount(amplifyAccount.client).toNum()
							: 0;

					const marketInterestData = allMarketInterestData[depositMarketIndex];
					const marketAprBreakdown = marketInterestData
						? [
								...marketInterestData.deposit.breakdown,
								...marketInterestData.borrow.breakdown,
							]
						: [];

					const marketMaxLeverage = MAX_LEVERAGE;

					return {
						poolId,
						fullMarketName:
							getCleanedMarketSymbol(depositMarketConfig.symbol) +
							'/' +
							getCleanedMarketSymbol(borrowMarketConfig.symbol),
						depositMarketConfig,
						borrowMarketConfig,
						// hide the -1 from the symbols
						depositMarketSymbol: getCleanedMarketSymbol(
							depositMarketConfig.symbol
						),
						borrowMarketSymbol: getCleanedMarketSymbol(
							borrowMarketConfig.symbol
						),
						maxApy: calculateApy(marketInPool.bankConfig.marketIndex, 5).netApy,
						maxApyBigNum: BigNum.fromPrint(
							`${calculateApy(marketInPool.bankConfig.marketIndex, 5).netApy}`,
							PERCENTAGE_PRECISION_EXP
						),
						maxLeverage: marketMaxLeverage,
						availableLiquidityQuote:
							borrowMarketData?.borrowLimitQuote ??
							BigNum.zero(QUOTE_PRECISION_EXP),
						availableLiquidityBase:
							borrowMarketData?.borrowLimitBase ??
							BigNum.zero(BASE_PRECISION_EXP),
						userDepositsQuote: userDepositBalance.quoteValue,
						userDepositsBase: userDepositBalance.balance,
						userBorrowsQuote: userBorrowBalance.quoteValue,
						userBorrowsBase: userBorrowBalance.balance,
						netValueQuote: userDepositBalance.quoteValue.sub(
							userBorrowBalance.quoteValue
						),
						yieldBreakdown: marketAprBreakdown,
						apyCalculator: calculateApy,
						userAccountData: amplifyAccount,
						userKey: amplifyAccount?.userKey,
						userLeverage: (amplifyMarginInfo?.leverage ?? 0) + 1,
						userPnl: userPnl,
					};
				});
		},
		[
			driftClientIsReady,
			driftClient,
			allMarketInterestData,
			poolId,
			amplifyAccount,
			amplifyMarginInfo,
			calculateApy,
			spotMarketsSortedByMarketIndex,
		]
	);

	const getCurrentUserAmplifyPosition = useCallback(() => {
		return getAmplifyMarketStats().find(
			(market) =>
				market.poolId === poolId &&
				market.userDepositsBase.gt(BigNum.zero(BASE_PRECISION_EXP)) &&
				market.userKey === userKey
		);
	}, [userKey, poolId]);

	return {
		getAmplifyMarketStats,
		getCurrentUserAmplifyPosition,
	};
};
